import type { DetectorStats } from "../core/blinkDetector.ts";
import { HEALTHY_RATE_RANGE, type SessionMetrics } from "../core/metrics.ts";
import type { DetectorEvent } from "../core/types.ts";

type Tone = "" | "good" | "warn" | "bad";

export interface LiveStats {
  fps: number;
  metrics: SessionMetrics;
  closure: number;
  yawDegrees: number;
  ipd: number;
  delegate: string;
  inferenceMs: number;
  inferenceSize: string;
  sourceFps: number;
  /** 0..1 pressure level. Off the HUD, but not worth losing entirely. */
  suppression: number;
  lowConfidence: boolean;
  recording: { active: boolean; frames: number; labels: number };
}

/**
 * The numeric readout grid.
 *
 * Blink rate is coloured against the 15–20/min reference range from the
 * literature — but note that this is a comparison, not a diagnosis. Spec §10
 * puts clinical claims firmly out of scope, and the difference between "your
 * rate is below the typical range" and "you have dry eye disease" is the
 * difference between a wellness app and a regulated medical device.
 */
export class Readouts {
  readonly #cells = new Map<string, HTMLElement>();

  constructor(private readonly root: HTMLElement) {}

  render(stats: LiveStats): void {
    const m = stats.metrics;
    const rateTone: Tone =
      m.ratePerMinute === 0
        ? ""
        : m.ratePerMinute < HEALTHY_RATE_RANGE[0]
          ? "warn"
          : m.ratePerMinute > HEALTHY_RATE_RANGE[1] * 1.6
            ? "warn"
            : "good";

    this.#set("blinks", String(m.totalComplete), "");
    this.#set("rate /min", m.ratePerMinute.toFixed(1), rateTone);
    this.#set("partial", `${(m.partialShare * 100).toFixed(0)}%`, "");
    this.#set("since last", `${(m.sinceLastBlinkMs / 1000).toFixed(1)}s`, "");
    this.#set("longest gap", `${(m.longestGapMs / 1000).toFixed(1)}s`, "");
    this.#set("duration", `${m.meanDurationMs.toFixed(0)}ms`, "");
    this.#set(
      "latency",
      `${m.meanLatencyMs.toFixed(0)}ms`,
      m.meanLatencyMs > 180 ? "warn" : m.meanLatencyMs > 0 ? "good" : "",
    );
    // Cumulative closed time counts closure the detector threw out, so a gap
    // between this and the blink count is itself the diagnosis.
    this.#set(
      "eyes closed",
      `${(m.closedMs / 1000).toFixed(1)}s · ${(m.closedShare * 100).toFixed(0)}%`,
      "",
    );
    this.#set(
      "suppression",
      `${Math.round(stats.suppression * 100)}%`,
      stats.suppression > 0.5 ? "warn" : "",
    );
    this.#set("closure", Number.isFinite(stats.closure) ? stats.closure.toFixed(2) : "—", "");
    this.#set("fps", stats.fps.toFixed(1), stats.fps < 10 ? "bad" : "");
    this.#set("yaw", `${stats.yawDegrees.toFixed(0)}°`, stats.lowConfidence ? "warn" : "");
    this.#set("ipd px", Number.isFinite(stats.ipd) ? stats.ipd.toFixed(0) : "—", "");
    this.#set("delegate", stats.delegate, "");
    // The real frame-rate ceiling. If this exceeds the frame budget (1000/fps),
    // the target rate is unreachable no matter what it is set to.
    this.#set(
      "infer ms",
      stats.inferenceMs > 0 ? stats.inferenceMs.toFixed(0) : "—",
      stats.inferenceMs > 55 ? "bad" : stats.inferenceMs > 35 ? "warn" : "good",
    );
    this.#set("infer size", stats.inferenceSize || "—", "");
    // The camera's own delivery rate, before throttling. Shown next to fps so a
    // gap between them reads as OUR decimation rather than the hardware's limit
    // — a distinction that cost nine sessions to notice was missing.
    this.#set(
      "source fps",
      Number.isFinite(stats.sourceFps) ? stats.sourceFps.toFixed(1) : "—",
      "",
    );
    this.#set(
      "recording",
      stats.recording.active
        ? `${(stats.recording.frames / Math.max(stats.fps, 1)).toFixed(0)}s · ${stats.recording.labels} taps`
        : "off",
      stats.recording.active ? "bad" : "",
    );
  }

  #set(label: string, value: string, tone: Tone): void {
    let cell = this.#cells.get(label);
    if (!cell) {
      const wrap = document.createElement("div");
      wrap.className = "stat";
      wrap.innerHTML = `<div class="stat-label"></div><div class="stat-value"></div>`;
      (wrap.firstElementChild as HTMLElement).textContent = label;
      this.root.append(wrap);
      cell = wrap.lastElementChild as HTMLElement;
      this.#cells.set(label, cell);
    }
    if (cell.textContent !== value) cell.textContent = value;
    cell.className = `stat-value${tone ? ` ${tone}` : ""}`;
  }
}

/**
 * Turns detector counters into a sentence about what is going wrong.
 *
 * "Nothing detected" is not a bug report. Each silent failure here has a
 * different cause and a different fix, and they are indistinguishable from the
 * outside — which is exactly the situation where someone testing on their own
 * phone can only report that it does not work. Naming the stage that ate the
 * signal is what makes remote feedback worth anything.
 */
export function explainSilence(
  stats: DetectorStats,
  ready: boolean,
  closeThreshold: number,
): { text: string; tone: Tone } {
  if (stats.framesSeen === 0) {
    return { text: "No frames from the camera yet.", tone: "bad" };
  }
  if (stats.framesWithFace === 0) {
    return {
      text: "Camera is running but no face is being found. More light on your face, or move back a little.",
      tone: "bad",
    };
  }
  if (!ready) {
    return {
      text: "Learning your open-eye baseline — blink normally for a few seconds.",
      tone: "",
    };
  }
  if (stats.emitted > 0) {
    const rejected = stats.suppressedByConfidence + stats.suppressedByDuration;
    return {
      text:
        rejected > 0
          ? `Detecting. ${stats.emitted} blinks, ${rejected} rejected (${stats.suppressedByConfidence} as movement).`
          : `Detecting normally. ${stats.emitted} blinks.`,
      tone: "good",
    };
  }
  if (stats.suppressedByConfidence > 0) {
    return {
      text: `${stats.suppressedByConfidence} blinks found but thrown out as head movement. Try bracing the phone against something.`,
      tone: "warn",
    };
  }
  if (stats.framesLowConfidence > stats.framesWithFace * 0.5) {
    return {
      text: "Too much movement between frames to trust the signal. Prop the phone up rather than holding it.",
      tone: "warn",
    };
  }
  if (stats.suppressedByDuration > 0) {
    return {
      text: `${stats.suppressedByDuration} closures were the wrong length to be blinks. Possible, but worth reporting.`,
      tone: "warn",
    };
  }
  if (stats.closuresStarted === 0) {
    return {
      text: `Face tracked, but your eyes never read as closing — signal peaked at ${stats.peakClosure.toFixed(2)} against a ${closeThreshold.toFixed(2)} threshold. This is the interesting failure: record it and export.`,
      tone: "bad",
    };
  }
  return { text: "Closures seen but none became blinks. Export a recording.", tone: "warn" };
}

const MAX_LOG_ROWS = 300;

export class EventLog {
  constructor(
    private readonly root: HTMLElement,
    /** Small for the on-camera terminal, large for the scrollable trail. */
    private readonly maxRows = MAX_LOG_ROWS,
  ) {}

  add(event: DetectorEvent): void {
    const { text, tone } = describe(event);
    if (!text) return;
    this.write(event.t, text, tone);
  }

  write(t: number, text: string, tone: string): void {
    const row = document.createElement("div");
    row.className = "log-row";
    row.innerHTML = `<span class="log-t"></span><span class="${tone}"></span>`;
    (row.firstElementChild as HTMLElement).textContent = `${(t / 1000).toFixed(2)}s`;
    (row.lastElementChild as HTMLElement).textContent = text;

    const atBottom = this.root.scrollTop + this.root.clientHeight >= this.root.scrollHeight - 24;
    this.root.append(row);
    while (this.root.childElementCount > this.maxRows) this.root.firstElementChild?.remove();
    if (atBottom) this.root.scrollTop = this.root.scrollHeight;
  }

  /**
   * Jumps to the newest row.
   *
   * A collapsed <details> gives every measurement zero, so the follow-the-tail
   * check in write() cannot tell "pinned to the bottom" from "scrolled away"
   * and the log reopens showing its oldest rows. Whoever reveals it has to say
   * so explicitly.
   */
  scrollToEnd(): void {
    this.root.scrollTop = this.root.scrollHeight;
  }

  clear(): void {
    this.root.replaceChildren();
  }
}

/**
 * One stream of rows, two views of it.
 *
 * The terminal over the camera shows the last few lines without you having to
 * look away from your own face — which is the only moment the lines are worth
 * anything live. The trail at the bottom keeps the scrollback for afterwards.
 * Writing to both from a single call is what keeps them from drifting apart.
 */
export class LogGroup {
  constructor(private readonly views: readonly EventLog[]) {}

  add(event: DetectorEvent): void {
    for (const view of this.views) view.add(event);
  }

  write(t: number, text: string, tone: string): void {
    for (const view of this.views) view.write(t, text, tone);
  }

  scrollToEnd(): void {
    for (const view of this.views) view.scrollToEnd();
  }

  clear(): void {
    for (const view of this.views) view.clear();
  }
}

function describe(event: DetectorEvent): { text: string; tone: string } {
  switch (event.type) {
    case "blink":
      return {
        text:
          `${event.kind} · ${event.eye} · ${event.duration.toFixed(0)}ms · ` +
          `amp ${event.amplitude.toFixed(2)} · lat ${event.latency.toFixed(0)}ms`,
        tone: event.kind === "complete" ? "log-blink" : "log-partial",
      };
    case "no-blink":
      return {
        text: `no blink for ${(event.elapsed / 1000).toFixed(1)}s`,
        tone: "log-alert",
      };
    case "squint":
      return {
        text: `squint sustained ${(event.duration / 1000).toFixed(1)}s @ ${event.intensity.toFixed(2)}`,
        tone: "log-alert",
      };
    case "face-lost":
      return { text: "face lost", tone: "log-alert" };
    case "face-found":
      return { text: "face acquired", tone: "log-info" };
    // Suppression ticks at 10Hz and pointer events fire on every press; both
    // would drown the log. They belong in the meter and the game, not here.
    case "suppression":
    case "pointer":
      return { text: "", tone: "" };
  }
}
